/**
 * Unwrap the first argument of a custom element event. Vue custom elements emit
 * `new CustomEvent(name, { detail: args })`, so a single-argument emit arrives as
 * `detail: [value]`. A non-array detail (a scalar or object payload) is returned
 * as-is rather than destructured.
 *
 * @param {CustomEvent} event
 * @returns {*}
 */
export function firstDetail(event) {
  const detail = event?.detail;
  return Array.isArray(detail) ? detail[0] : detail;
}

/**
 * Whether an `input` event is the element's own report. The sp text controls carry the text in a
 * `detail` array; Vue's `vModelText` re-dispatches a plain `input` when a composition ends, which
 * carries none and duplicates an edit the element has already reported.
 *
 * @param {Event} event An `input` event from an sp-* text control.
 * @returns {boolean} True when the element reported it.
 */
export function isElementReport(event) {
  return Array.isArray(event?.detail);
}

/**
 * Whether a keyboard event belongs to an input-method composition, such as the Enter that confirms
 * a candidate. WebKit reports that confirming key with `isComposing` false and `keyCode` 229.
 *
 * @param {KeyboardEvent | null | undefined} event
 * @returns {boolean} False when `event` is missing.
 */
export function isComposing(event) {
  return Boolean(event?.isComposing) || event?.keyCode === 229;
}

const INTERACTIVE_CONTENT = [
  "a[href]",
  "audio[controls]",
  "button",
  "details",
  "embed",
  "iframe",
  "input",
  "select",
  "summary",
  "textarea",
  "video[controls]",
  "[contenteditable]:not([contenteditable=false])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * Whether the event passed through interactive content on its way to `boundary`.
 *
 * A label activates the control it labels, except when the click landed on interactive content
 * inside it — a link there navigates instead. `composedPath` is what makes this work across a
 * slot: the entries below `boundary` are the slotted elements the click actually crossed.
 *
 * @param {Event} event
 * @param {EventTarget} boundary - Where to stop walking, typically the handler's `currentTarget`.
 * @returns {boolean}
 */
export function crossedInteractiveContent(event, boundary) {
  for (const target of event.composedPath()) {
    if (target === boundary) {
      return false;
    }
    if (target instanceof Element && target.matches(INTERACTIVE_CONTENT)) {
      return true;
    }
  }

  return false;
}
